// Shared calendar types
// Base event shape shared by all time-block items in the calendar
export interface EventBase {
  id: number;
  doctorId?: number | null;
  doctor?: { firstName: string; lastName: string; color?: string };
  startTime: string | null;
  endTime: string | null;
  duration?: number;
  notes: string;
}

// Appointment is a specialized type of Event with patient metadata and optional colors
export interface AppointmentTreatmentLink {
  id: number;
  name: string;
  cost?: number;
  recommendedPrice?: number;
  additionalUseFee?: number;
  doctorId?: number | null;
  payments?: { amount: number }[];
  installments?: { dueDate: string; paid: boolean }[];
}

export interface AppointmentTreatmentTemplateLink {
  id: number;
  name: string;
  suggestedPrice?: number;
  suggestedCost?: number;
  expectedDurationMinutes?: number;
  suggestedAdditionalUseFee?: number;
  defaultDoctorId?: number | null;
}

export interface AppointmentPaymentSummary {
  id: number;
  amount: number;
  kind?: 'payment' | 'refund';
  date?: string;
  method?: string;
  notes?: string;
  treatmentId?: number | null;
  receiptNumber?: string | null;
  payerType?: string | null;
  payerName?: string | null;
  paymentGroupId?: string | null;
  allocationMode?: string | null;
  appliedTo?: string | null;
}

export type AppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'arrived'
  | 'seated'
  | 'ready_for_checkout'
  | 'completed'
  | 'canceled';

export type AppointmentPaymentStatus = 'unpaid' | 'partially_paid' | 'paid' | 'refunded';

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  scheduled: '#64748b',
  confirmed: '#3b82f6',
  arrived: '#f59e0b',
  seated: '#8b5cf6',
  ready_for_checkout: '#ec4899',
  completed: '#10b981',
  canceled: '#991b1b'
};

export interface TreatmentInProgress {
  id: number;
  name: string;
  cost: number;
  paid: number;
  due: number;
  createdAt?: string | null;
  nextDueDate?: string | null;
  doctorId?: number | null;
  secondDoctorId?: number | null;
}

export interface CalendarPayment {
  id: number;
  patientId: number;
  patientName: string;
  patientHc?: string | null;
  treatmentId: number;
  treatmentName: string;
  doctorId?: number | null;
  secondDoctorId?: number | null;
  date: string;
  amount: number;
  method: string;
  notes?: string | null;
}

export interface Laboratory {
  id: number;
  name: string;
}

export interface TreatmentFinancialReminder {
  treatmentId: number;
  treatmentName: string;
  totalDue: number;
  pastDue: number;
  dueToday: number;
  futureScheduled: number;
  treatmentDate?: string | null;
  nextDueDate?: string | null;
  isLinkedToAppointment: boolean;
}

export interface AppointmentFinancialSummary {
  patientBalanceDue: number;
  pastDue: number;
  dueToday: number;
  futureScheduled: number;
  totalCharges: number;
  totalPaid: number;
  hasPastDue: boolean;
  hasDueToday: boolean;
  hasPendingBalance: boolean;
  treatments: TreatmentFinancialReminder[];
}

export interface ReservationFeeSummary {
  id: number;
  amount: number;
  paidAmount: number;
  balance: number;
  availableCredit: number;
  status: 'pending' | 'paid' | 'waived' | 'refunded';
  originAppointmentId?: number | null;
}
export interface Appointment extends EventBase {
  patientId: number | null;
  patient?: { name: string; email: string; hc?: string; profilePicturePath?: string | null; status?: string | number };
  secondDoctorId?: number | null;
  secondDoctor?: { firstName: string; lastName: string; color?: string };
  color?: string | null;
  marginColor?: string | null;
  room?: number | null;
  status?: AppointmentStatus | 'stand-by' | 'all-day' | null;
  isAllDay?: boolean;
  chargeAmount?: number | null;
  reservationFeeAmount?: number | null;
  reservationFee?: ReservationFeeSummary | null;
  reservationFeePaidAmount?: number;
  reservationFeeBalance?: number;
  isReservationFeePaid?: boolean;
  paymentStatus?: AppointmentPaymentStatus;
  checkoutNote?: string | null;
  arrivedAt?: string | null;
  seatedAt?: string | null;
  readyForCheckoutAt?: string | null;
  completedAt?: string | null;
  canceledAt?: string | null;
  canceledByUserId?: number | null;
  canceledBy?: string | null;
  cancellationNote?: string | null;
  isLateCancellation?: boolean;
  cancellationLeadTimeMinutes?: number | null;
  isNoShow?: boolean;
  isProspect?: boolean;
  prospectEventId?: number;
  prospectName?: string | null;
  prospectContact?: string | null;
  prospectNotes?: string | null;
  treatmentTemplateId?: number | null;
  treatmentTemplate?: AppointmentTreatmentTemplateLink | null;
  customReason?: string | null;
  treatmentId?: number | null;
  treatment?: AppointmentTreatmentLink | null;
  treatments?: AppointmentTreatmentLink[];
  payments?: AppointmentPaymentSummary[];
  amountPaid?: number;
  financialSummary?: AppointmentFinancialSummary | null;
  treatedTeeth?: string | null;
}

export interface TreatmentTemplateDoctorSummary {
  id: number;
  firstName: string;
  lastName: string;
}

export interface TreatmentSpecialty {
  id: number;
  code: string;
  name: string;
  nameEs: string;
  templateCount?: number;
}

export interface TreatmentTemplate {
  id: number;
  name: string;
  nameEs?: string | null;
  specialtyId?: number | null;
  specialty?: TreatmentSpecialty | null;
  suggestedPrice: number;
  suggestedCost: number;
  expectedDurationMinutes: number;
  suggestedAdditionalUseFee: number;
  defaultDoctorId?: number | null;
  doctors: TreatmentTemplateDoctorSummary[];
}

export interface TreatmentTemplateDoctorStats {
  doctorId: number;
  firstName: string;
  lastName: string;
  appointmentCount: number;
  averageDurationMinutes?: number | null;
  averageCost?: number | null;
  totalCost: number;
}

export interface TreatmentTemplateStats {
  id: number;
  name: string;
  appointmentCount: number;
  completedAppointmentCount: number;
  averageDurationMinutes?: number | null;
  averageCost?: number | null;
  totalCost: number;
  doctorStats: TreatmentTemplateDoctorStats[];
}

// CalendarEvent is the generic event type with a title
export interface CalendarEvent extends EventBase {
  title: string;
  isNewPatientIntake?: boolean;
  prospectName?: string | null;
  prospectContact?: string | null;
  prospectNotes?: string | null;
  recurrenceRule?: string | null;
  recurrenceEndDate?: string | null;
}

export interface Patient {
  id: number;
  hc?: string;
  name: string;
  presentedName?: string;
  email: string;
  profilePicturePath?: string | null;
  dateOfBirth?: string;
  phoneNumber?: string;
  notes?: string;
  nextContact: string | null;
  hasCriticalAllergy: boolean;
  noShowCount: number;
  cancellationCount?: number;
  lateCancellationCount?: number;
  balanceDue?: number;
  status?: string | number;
  treatments?: {
    id: number;
    cost: number;
    payments?: { amount: number }[];
  }[];
  payments?: { amount: number }[];
}
export interface Doctor { id: number; firstName: string; lastName: string; color?: string; isHidden?: boolean; defaultRoom?: number | null; commissionPercentage?: number | null }

export interface CalendarDetailCharge {
  id: string;
  concept: string;
  dueDate: string;
  charge: number;
  paid: number;
  reservationCreditApplied?: number;
  reservationPendingOffset?: number;
  pending: number;
  treatmentId?: number | null;
  accountEntryId?: number | null;
  isCurrentAppointment: boolean;
}

export interface CalendarDetailTreatment {
  treatmentId: number;
  treatmentName: string;
  treatmentCategory?: string | null;
  executionStatus: 'planned' | 'performed_today' | 'scheduled_later';
  status: string;
  quantity: number;
  price: number;
  discount: number;
  discountMode?: 'amount' | 'percent';
  discountValue?: number;
  discountAmount?: number;
  isIncludedFree: boolean;
  doctorId?: number | null;
  secondDoctorId?: number | null;
  primaryDoctorCommissionPercentage?: number | null;
  secondDoctorCommissionPercentage?: number | null;
  grossPrice?: number | null;
  recommendedPrice?: number | null;
  additionalUseFee?: number;
  isSpecialty?: boolean;
  doctor?: string | null;
  paid: number;
  reservationCreditApplied?: number;
  reservationPendingOffset?: number;
  due: number;
  notes?: string | null;
  invoice?: boolean;
  specialMaterialsCost?: number;
  laboratoryId?: number | null;
  laboratoryCost?: number;
  mechanicName?: string | null;
  treatedTeeth?: string | null;
}

export interface CalendarDetailPayment {
  id: number;
  appointmentId?: number | null;
  paymentGroupId?: string | null;
  patientReservationFeeId?: number | null;
  date: string;
  amount: number;
  method: string;
  receiptNumber?: string | null;
  appliedTo?: string | null;
  payerType?: string | null;
  payerName?: string | null;
  notes?: string | null;
}

export interface CalendarDetailLedgerRow {
  date: string;
  concept: string;
  charge: number;
  payment: number;
  balance: number;
}

export interface CalendarDetailHistoryRow {
  id: number;
  startTime?: string | null;
  doctor?: string | null;
  treatments: string[];
  notes?: string | null;
}

export interface CalendarAppointmentDetail {
  appointment: {
    id: number;
    patientId?: number | null;
    doctorId?: number | null;
    secondDoctorId?: number | null;
    primaryDoctorCommissionPercentage?: number | null;
    secondDoctorCommissionPercentage?: number | null;
    room?: number | null;
    startTime?: string | null;
    endTime?: string | null;
    duration?: number | null;
    notes?: string | null;
    treatedTeeth?: string | null;
    status?: AppointmentStatus | string | null;
    isNoShow?: boolean;
    canceledAt?: string | null;
    statusLabel: string;
    appointmentType: string;
  };
  patient?: {
    id: number;
    name: string;
    hc?: string | null;
    firstName?: string | null;
    secondName?: string | null;
    firstLastName?: string | null;
    secondLastName?: string | null;
    presentedName?: string | null;
    email?: string | null;
    status?: string | number | null;
    statusName?: string | null;
    isForeignPatient?: boolean;
    profilePicturePath?: string | null;
    phoneNumber?: string | null;
    dateOfBirth?: string | null;
    address?: string | null;
    referralSource?: string | null;
    nextContact?: string | null;
    notes?: string | null;
    hasCriticalAllergy?: boolean;
    allergies?: string[];
  } | null;
  doctor?: { id: number; name: string } | null;
  secondDoctor?: { id: number; name: string } | null;
  financial: {
    previousDebt: number;
    currentAppointment: number;
    currentReservation?: number;
    totalDue: number;
    charges: CalendarDetailCharge[];
  };
  reservationFee?: ReservationFeeSummary | null;
  treatments: CalendarDetailTreatment[];
  payments: CalendarDetailPayment[];
  ledger: CalendarDetailLedgerRow[];
  history: CalendarDetailHistoryRow[];
}

export type HoursRange = { start: number; end: number };

export interface IntakeConversionContext {
  eventId: number;
  startTime: string;
  endTime: string;
  doctorId?: number | null;
  title?: string;
  notes?: string;
  prospectName?: string | null;
  prospectContact?: string | null;
  prospectNotes?: string | null;
}

export const ROOM_COLORS: Record<number, string> = {
  1: '#EF4444', // Red
  2: '#F59E0B', // Amber
  3: '#10B981', // Emerald
  4: '#3B82F6', // Blue
  5: '#8B5CF6'  // Violet
};
